import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#082f49',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#7dd3fc',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#0284c7',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#0369a1',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const CaravanBoatLpgSafetyRecordPDF = ({ formData = {} }) => {
  const eng = formData.engineerDetails || {};
  const craft = formData.craftOrCaravanDetails || {};
  const locker = formData.lpgLockerAndSupply || {};
  const pipe = formData.pipeworkAndTightness || {};
  const app = formData.appliances || {};
  const sign = formData.verdictAndSignatures || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Caravan & Marine Boat LPG Gas Safety Record</Text>
            <Text style={styles.headerSubtitle}>BS EN ISO 10239 (Marine), BS EN 1949 (Caravans) & Boat Safety Scheme (BSS)</Text>
          </View>
          <View style={styles.badge}>
            <Text>GAS SAFE REGISTER</Text>
          </View>
        </View>

        {/* 1 & 2: Engineer & Vessel/Caravan */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Gas Safe Registered Engineer</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engineer Name:</Text>
              <Text style={styles.fieldValue}>{eng.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Gas Safe ID:</Text>
              <Text style={styles.fieldValue}>{eng.gasSafeNumber || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Accreditation:</Text>
              <Text style={styles.fieldValue}>{eng.boatOrCaravanAccreditation || 'CCLP1 (Boats/Caravans)'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company & Tel:</Text>
              <Text style={styles.fieldValue}>{eng.companyName || 'N/A'} • {eng.telephone || ''}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Vessel / Caravan Identification</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Craft / Vehicle Type:</Text>
              <Text style={styles.fieldValue}>{craft.craftType || 'Marine Vessel'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Name / Reg / BSS No:</Text>
              <Text style={styles.fieldValue}>{craft.craftNameOrReg || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Mooring / Site:</Text>
              <Text style={styles.fieldValue}>{craft.mooringOrSiteLocation || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Owner / Date:</Text>
              <Text style={styles.fieldValue}>{craft.ownerName || 'N/A'} ({craft.inspectionDate || 'N/A'})</Text>
            </View>
          </View>
        </View>

        {/* 3: Marine / Caravan Gas Locker */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Marine / Caravan LPG Locker & Supply Integrity</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Locker Vapour-Tight:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{locker.gasLockerVapourTight || 'Yes - Vapour-Tight'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Overboard Drain:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{locker.overboardDrainClear || 'Pass - Clear'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cylinders Restrained:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{locker.cylinderSecuredUpright || 'Yes'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Marine Regulator:</Text>
                <Text style={styles.fieldValue}>{locker.marineRegulatorType || 'Marine Grade 30mbar'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Hose & Expiry Date:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{locker.flexibleHoseConditionAndDate || 'Pass - In Date'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Bubble Leak Tester:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{locker.bubbleLeakDetectorFitted || 'Pass'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4: Pipework & Tightness */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>4. Pipework, Isolation & Tightness Testing</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Pipework Material:</Text>
                <Text style={styles.fieldValue}>{pipe.pipeworkMaterial || 'Seamless Copper'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Isolation Valves:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{pipe.emergencyShutOffAccessible || 'Yes - Accessible'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Tightness Test Result:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{pipe.tightnessTestResult || 'Pass (0.0 mbar drop)'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>CO Alarm (BS EN 50291-2):</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{pipe.coAlarmFitted || 'Yes - In Date'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 5: Marine / Caravan Appliances */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>5. LPG Appliances & Room-Sealed Flues (100% Flame Failure Supervision)</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cooker / Hob:</Text>
                <Text style={styles.fieldValue}>{app.cookerMakeModel || 'LPG Cooker'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cooker FFD Check:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{app.cookerFfd || 'Pass (100% FFD)'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Water Heater / Flue:</Text>
                <Text style={styles.fieldValue}>{app.waterHeaterMakeModel || 'N/A'} ({app.waterHeaterFlueAndFfd || 'Pass'})</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Space Heater / Flue:</Text>
                <Text style={styles.fieldValue}>{app.spaceHeaterMakeModel || 'N/A'} ({app.spaceHeaterFlueAndFfd || 'Pass'})</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>3-Way Refrigerator:</Text>
                <Text style={styles.fieldValue}>{app.refrigeratorMakeModel || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Fridge Room-Sealed:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{app.refrigeratorRoomSealed || 'Pass'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 6: Assessment & Verdict */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Overall Statutory Safety Assessment</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Compliance Verdict:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#0369a1' }]}>{sign.overallAssessment || 'SATISFACTORY'}</Text>
          </View>
          <Text style={{ fontSize: 6.5, color: '#334155', marginTop: 2 }}>
            {sign.remedialWorkNotes || 'Marine LPG system inspected in full compliance with BS EN ISO 10239 and Boat Safety Scheme (BSS) standards.'}
          </Text>
          <View style={[styles.fieldRow, { marginTop: 3 }]}>
            <Text style={styles.fieldLabel}>Next Inspection Due:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold' }]}>{sign.nextInspectionDueDate || '12 Months'}</Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Gas Safe Marine / Caravan Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{eng.engineerName || 'Gas Safe Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Gas Safe Marine / Boat Safety Scheme Stamp</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{craft.inspectionDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Vessel / Caravan Owner Acceptance</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{craft.ownerName || 'Owner'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Safety certificate received</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{craft.inspectionDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            BS EN ISO 10239 / BS EN 1949 • Caravan & Marine Boat LPG Safety Record
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default CaravanBoatLpgSafetyRecordPDF;
